import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { FaSearch } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  GRANT_VIP,
  SEARCH_VIP_CANDIDATES,
} from "../../../queries/mutation/broadcaster/vipQueries";

const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_DAYS = 365;
const PRESETS = [7, 30, 90];

export type GrantTarget = {
  userId: string | null;
  tipperId: string | null;
  label: string;
  grantUntil: string | null;
};

type Props = {
  // Preselected from a row; otherwise the broadcaster searches for someone.
  target?: GrantTarget;
  onClose: () => void;
  onGranted: () => void;
};

const formatEnd = (ms: number) =>
  new Date(ms).toLocaleString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const GrantVipModal = ({ target, onClose, onGranted }: Props) => {
  const [picked, setPicked] = useState<GrantTarget | null>(target ?? null);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [daysText, setDaysText] = useState("30");

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, loading: searching } = useQuery(SEARCH_VIP_CANDIDATES, {
    variables: { query: debounced },
    skip: !!picked || debounced.length < 2,
    fetchPolicy: "network-only",
  });
  const [grant, { loading: granting }] = useMutation(GRANT_VIP);

  const days = Number(daysText) || 0;
  const validDays = Number.isInteger(days) && days >= 1 && days <= MAX_DAYS;
  // Days stack onto whatever's left, same as the server.
  const currentEnd = picked?.grantUntil ? new Date(picked.grantUntil).getTime() : 0;
  const newEnd = Math.max(Date.now(), currentEnd) + days * DAY_MS;

  const handleGrant = async () => {
    if (!picked) return;
    try {
      await grant({
        variables: { userId: picked.userId, tipperId: picked.tipperId, days },
      });
      toast.success(`${picked.label} is VIP until ${formatEnd(newEnd)}`);
      onGranted();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const candidates = data?.searchVipCandidates ?? [];

  return (
    <div className="modal modal-open">
      <div className="modal-box">
        <h3 className="font-bold text-lg">Grant VIP</h3>

        {!picked ? (
          <>
            <label className="input input-bordered flex items-center gap-2 mt-4">
              <FaSearch className="opacity-50" />
              <input
                type="search"
                className="grow"
                placeholder="Reinstorm or CB username"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </label>
            <div className="mt-3 max-h-72 overflow-y-auto">
              {debounced.length < 2 ? null : searching ? (
                <span className="loading loading-dots loading-sm" />
              ) : candidates.length === 0 ? (
                <p className="text-sm text-base-content/60">
                  Nobody matches "{debounced}"
                </p>
              ) : (
                <ul className="space-y-1">
                  {candidates.map((c: any) => (
                    <li key={c.userId ?? c.tipperId}>
                      <button
                        className="w-full text-left p-2 rounded-box hover:bg-base-200"
                        onClick={() =>
                          setPicked({
                            userId: c.userId,
                            tipperId: c.tipperId,
                            label: c.username,
                            grantUntil: c.grantUntil,
                          })
                        }
                      >
                        <div className="font-semibold">{c.username}</div>
                        <div className="text-xs text-base-content/60">
                          {c.reinUsername ? "Reinstorm account" : "CB tipper"}
                          {c.reinUsername && c.cbUsername
                            ? ` · CB ${c.cbUsername}`
                            : ""}
                          {c.grantUntil
                            ? ` · VIP until ${formatEnd(new Date(c.grantUntil).getTime())}`
                            : ""}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="modal-action">
              <button className="btn" onClick={onClose}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="mt-3">
              <span className="font-semibold">{picked.label}</span>
              {picked.grantUntil && (
                <span className="text-sm text-base-content/60">
                  {" "}
                  · VIP until{" "}
                  {formatEnd(new Date(picked.grantUntil).getTime())}
                </span>
              )}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <input
                type="text"
                inputMode="numeric"
                value={daysText}
                onChange={(e) => setDaysText(e.target.value.replace(/\D/g, ""))}
                aria-label="Days of VIP"
                className="input input-bordered w-24"
              />
              <span>days</span>
              {PRESETS.map((n) => (
                <button
                  key={n}
                  className={`btn btn-sm ${days === n ? "btn-primary" : "btn-outline"}`}
                  onClick={() => setDaysText(String(n))}
                >
                  {n}
                </button>
              ))}
            </div>
            {validDays ? (
              <p className="text-sm mt-3">
                {picked.grantUntil ? "Extends VIP to " : "VIP until "}
                <span className="font-semibold text-success">
                  {formatEnd(newEnd)}
                </span>
              </p>
            ) : (
              <p className="text-xs text-error mt-3">
                Pick between 1 and {MAX_DAYS} days
              </p>
            )}
            <p className="text-xs text-base-content/60 mt-2">
              They get the Discord VIP role if they have Discord linked, and a
              DM a few days before it ends.
            </p>
            <div className="modal-action">
              <button
                className="btn"
                onClick={() => (target ? onClose() : setPicked(null))}
                disabled={granting}
              >
                {target ? "Cancel" : "Back"}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleGrant}
                disabled={!validDays || granting}
              >
                {granting ? "Granting..." : `Grant ${days || ""} days`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GrantVipModal;
